<template>
    <b-modal id="labelModal" @hide="resetFields" @show="init">
        <template v-slot:modal-title>
            <div class="d-flex align-items-center">
                <font-awesome-icon class="color-brand mr-2" :icon="['fal', 'plus']" />
                <span class="color-brand modal-title">{{
                    isEdit ? $t('admin.label_modal_edit_title') : $t('admin.label_modal_create_title')
                }}</span>
            </div>
        </template>
        <template>
            <b-alert class="mt-2" variant="info" show>
                <font-awesome-icon :icon="['far', 'square-info']" size="xl" class="mr-2"></font-awesome-icon>
                <i18n path="admin.icon_disclaimer">
                    <a href="https://fontawesome.com/search?ip=classic&s=light&o=r" target="_blank">
                        {{ $t('admin.icon_disclaimer_link') }}
                    </a>
                </i18n>
            </b-alert>
            <form id="labelForm" @submit.prevent="submit">
                <label for="name">{{ $t('admin.label_modal_name') }}</label>
                <b-form-input
                    id="name"
                    v-model="name"
                    :disabled="isEdit"
                    type="text"
                    class="mt-2 mb-4"
                    @focus="error = null"
                    :placeholder="$t('admin.label_modal_name_placeholder')"
                />
                <label for="translation">{{ $t('admin.label_modal_translation') }}</label>
                <b-form-input
                    id="translation"
                    v-model="translation"
                    type="text"
                    class="mt-2 mb-4"
                    @focus="error = null"
                    :placeholder="$t('admin.label_modal_translation_placeholder')"
                />
                <label for="signBankIds">{{ $t('admin.label_modal_signbankids') }}</label>
                <b-form-input
                    id="signBankIds"
                    v-model="signBankIdsString"
                    type="text"
                    class="mt-2 mb-2"
                    @focus="error = null"
                    :placeholder="$t('admin.label_modal_signbankids_placeholder')"
                />
                <small class="text-muted mb-4 d-block">{{ $t('admin.label_modal_signbankids_help') }}</small>
                <label for="infotext">{{ $t('admin.label_modal_infotext') }}</label>
                <b-form-input
                    id="infotext"
                    v-model="infoText"
                    type="text"
                    class="mt-2 mb-4"
                    @focus="error = null"
                    :placeholder="$t('admin.label_modal_infotext_placeholder')"
                />
                <label for="icon">{{ $t('admin.label_modal_icon') }}</label>
                <b-form-input
                    id="icon"
                    v-model="icon"
                    type="text"
                    class="mt-2 mb-4"
                    @focus="error = null"
                    :placeholder="$t('admin.label_modal_icon_placeholder')"
                />
                <IconPreview :icon="icon" :translation="translation" :info="infoText" />
                <b-alert v-if="error" variant="error" show>
                    <font-awesome-icon :icon="['far', 'square-exclamation']" size="xl" class="mr-2"></font-awesome-icon>
                    {{ error }}
                </b-alert>
            </form>
        </template>
        <template v-slot:modal-footer>
            <div class="d-flex">
                <button type="submit" form="labelForm" class="vgtc-button mr-4">
                    {{ isEdit ? $t('general.edit') : $t('general.create') }}
                </button>
                <button @click="hideModal" class="vgtc-button vgtc-button--invert">
                    {{ $t('general.cancel') }}
                </button>
            </div>
        </template>
    </b-modal>
</template>

<script lang="ts">
import { Constants } from '@/shared/Constants';
import { axiosHttp } from '@/util/AxiosHttp';
import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator';
import { ILabel } from '../../../../functions/src/shared/contracts/api/MasterdataContract';
import IconPreview from './IconPreview.vue';

@Component({ components: { IconPreview } })
export default class LabelModal extends Vue {
    public name: string = '';
    public translation: string = '';
    public signBankIdsString: string = '';
    public icon: string = '';
    public infoText: string = '';

    @Prop() readonly label!: ILabel;

    public error: string | null = null;

    get signBankIds(): number[] {
        if (!this.signBankIdsString.trim()) return [];
        return this.signBankIdsString.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
    }

    set signBankIds(value: number[]) {
        this.signBankIdsString = value.join(', ');
    }

    @Watch('label', { immediate: true })
    public init() {
        if (this.label && this.label.id) {
            // Editing existing label
            this.name = this.label.id;
            this.translation = this.label.name;
            this.signBankIds = this.label.signBankIds || [];
            this.icon = this.label.icon || '';
            this.infoText = this.label.info || '';
        } else {
            // Creating new label - ensure fields are empty
            this.name = '';
            this.translation = '';
            this.signBankIdsString = '';
            this.icon = '';
            this.infoText = '';
        }
        this.error = null;
    }

    public resetFields() {
        this.name = '';
        this.translation = '';
        this.signBankIdsString = '';
        this.icon = '';
        this.error = null;
        this.infoText = '';
    }

    get isEdit(): boolean {
        return this.label && this.label.id !== '';
    }

    public async createLabel() {
        try {
            this.error = '';
            await axiosHttp.http.post(`${Constants.baseOperationsUrl}/labels`, {
                name: this.name,
                translation: this.translation,
                signBankIds: this.signBankIds,
                icon: this.icon,
                info: this.infoText,
            });
            this.hideModal();
        } catch (error) {
            this.error = `${this.$t('admin.label_creation_error')}: ${error}`;
        }
    }

    public async editLabel() {
        this.error = null;

        try {
            await axiosHttp.http.put<ILabel>(`${Constants.baseOperationsUrl}/labels/${this.label.id}`, {
                id: this.name,
                translation: this.translation,
                signBankIds: this.signBankIds,
                icon: this.icon,
                info: this.infoText,
            });

            this.hideModal();
        } catch (error) {
            this.error = `${this.$t('admin.label_modal_error_update_failed')}: ${error}`;
        }
    }

    public showModal() {
        this.$bvModal.show('labelModal');
    }

    public hideModal() {
        this.$bvModal.hide('labelModal');
    }

    @Emit()
    public async submit() {
        if (this.isEdit) {
            await this.editLabel();
        } else {
            await this.createLabel();
        }
    }
}
</script>
